import { useCallback, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import DateVO from '../../../../../../../../../vo/DateVO'
import GanttDate, { DAY_MS } from './ganttDate'
import objects from '../../../../../../../../../utils/objects'
import { CalendarDateVO } from '../../../../../../../../../domain/value-object/CalendarDateVO'
import { IRowNode } from 'ag-grid-community'
import { GanttDisplayUnit } from '../../../../../../../../../domain/value-object/GanttParameterVO'

const MIN_GAP_BY_UNIT: Record<GanttDisplayUnit, number> = {
  [GanttDisplayUnit.DAY]: 1,
  [GanttDisplayUnit.WEEK]: 3,
  [GanttDisplayUnit.MONTH]: 15,
  [GanttDisplayUnit.QUARTER]: 45,
}

export const useGanttIndex = (
  ganttDate: GanttDate,
  displayUnit?: GanttDisplayUnit
) => {
  const [value, setValue] = useState<number[]>([])
  const [startDisabled, setStartDisabled] = useState(false)
  const [endDisabled, setEndDisabled] = useState(false)

  const moveWith = useRef<'track' | 'thumb' | undefined>()
  const previousValue = useRef<number[]>([])
  const startValue = useRef<number[]>([])
  const grabbedPosValue = useRef<number>(0)
  const selectedRows = useRef<IRowNode[]>([])
  const workDays = useRef<number>(0)
  const dragStartDate = useRef<Date | null>(null)
  const dragEndDate = useRef<Date | null>(null)
  const activeThumbRef = useRef<number>(0)

  const initialize = useCallback(
    (start, end) => {
      setValue([start, end])
      setStartDisabled(!ganttDate.indexIsActive(start))
      setEndDisabled(!ganttDate.indexIsActive(end))
    },
    [ganttDate]
  )

  const moveWithTrack = useCallback(
    rows => {
      moveWith.current = 'track'
      startValue.current = value.concat()
      previousValue.current = value.concat()
      selectedRows.current = rows
      workDays.current = ganttDate.getWorkDaysByIndex(value)
      dragStartDate.current = ganttDate.startDate.date
      dragEndDate.current = ganttDate.endDate.date
    },
    [value, ganttDate]
  )

  const setMoveWithTrackOrigin = useCallback(
    (indexes: number[], activeThumnbIdenx: number) => {
      if (moveWith.current === 'track') {
        activeThumbRef.current = activeThumnbIdenx
        grabbedPosValue.current = indexes[activeThumnbIdenx]
      }
    },
    []
  )

  const moveWithThumb = useCallback(
    rows => {
      moveWith.current = 'thumb'
      startValue.current = value.concat()
      previousValue.current = value.concat()
      selectedRows.current = rows
    },
    [value]
  )
  const getMovingRows = useCallback(() => selectedRows.current, [selectedRows])

  const moveThrottleWithTrack = useCallback(
    (indexes: number[], activeIndex: number) => {
      const diff = indexes[activeThumbRef.current] - grabbedPosValue.current
      grabbedPosValue.current = indexes[activeThumbRef.current]
      if (diff === 0) {
        return
      }

      const maxIndex = ganttDate.getMaxIndex()
      let val = [startValue.current[0] + diff, startValue.current[1] + diff]

      if (startDisabled && val[0] < previousValue.current[0]) {
        const adjust = previousValue.current[0] - val[0]
        val = [val[0] + adjust, val[1] + adjust]
        grabbedPosValue.current += adjust
      }
      if (endDisabled && val[1] > previousValue.current[1]) {
        const adjust = previousValue.current[1] - val[1]
        val = [val[0] + adjust, val[1] + adjust]
        grabbedPosValue.current += adjust
      }
      if (!startDisabled && val[0] < 0) {
        const adjust = -val[0]
        val = [val[0] + adjust, val[1] + adjust]
        grabbedPosValue.current += adjust
      }
      if (!endDisabled && maxIndex < val[1]) {
        const adjust = maxIndex - val[1]
        val = [val[0] + adjust, val[1] + adjust]
        grabbedPosValue.current += adjust
      }

      if (
        !startDisabled &&
        !endDisabled &&
        ganttDate.indexIsActive(val[0]) &&
        ganttDate.indexIsActive(val[1])
      ) {
        // Skip holiday
        const startDate = ganttDate.getStartDateByIndex(val[0])
        const endDate = ganttDate.getEndDateByIndex(val[1])
        const newWorkDays = ganttDate.getWorkDaysByIndex(val)
        const workDayDiff = workDays.current - newWorkDays
        if (workDayDiff) {
          if (0 < diff) {
            const newDate =
              ganttDate.getWorkDay(endDate, workDayDiff) ?? endDate
            const newIndex = ganttDate.findIndex(newDate)
            val[1] = newIndex + 1
          } else {
            const newDate =
              ganttDate.getWorkDay(startDate, -workDayDiff) ?? startDate
            val[0] = ganttDate.findIndex(newDate)
          }
        }
      }
      setValue(val)
      startValue.current = val
    },
    [ganttDate, startDisabled, endDisabled]
  )

  const moveThrottle = useCallback(
    (indexes: number[], activeIndex: number) => {
      if (moveWith.current === 'track') {
        moveThrottleWithTrack(indexes, activeIndex)
        return
      }
      const val = [...indexes]
      const minGap = MIN_GAP_BY_UNIT[displayUnit ?? GanttDisplayUnit.DAY]
      const maxIndex = ganttDate.getMaxIndex()
      if (activeIndex === 0 && val[0] >= val[1]) {
        val[1] = Math.min(val[0] + minGap, maxIndex)
      } else if (activeIndex === 1 && val[1] <= val[0]) {
        val[0] = Math.max(val[1] - minGap, 0)
      }
      setValue(val)
    },
    [ganttDate, moveThrottleWithTrack, displayUnit]
  )

  const move = useMemo(
    () =>
      _.throttle(
        (indexes: number[], activeIndex: number) =>
          moveThrottle(indexes, activeIndex),
        100
      ),
    [moveThrottle]
  )

  const changeSelectedRows = useCallback(
    (dataPath: string, skipUuid: string, calendarDaysDiff?: number) => {
      // Update scheduled date term of selected rows
      if (!selectedRows.current || selectedRows.current.length <= 1) return []
      const rows: any[] = []
      const today = DateVO.now().toDate()
      selectedRows.current.forEach(projectPlanRowNode => {
        const projectPlanRow = projectPlanRowNode.data
        if (skipUuid === projectPlanRow.uuid) return
        const row = _.cloneDeep(projectPlanRow)
        row.isEdited = true
        const dateTerm = objects.getValue(
          row,
          `${dataPath}wbsItem`
        ).scheduledDate
        let startDate: CalendarDateVO
        let endDate: CalendarDateVO
        if (calendarDaysDiff != null) {
          const offsetMs = calendarDaysDiff * DAY_MS
          const startMs = new Date(
            dateTerm?.startDate || dateTerm?.endDate || today
          ).getTime()
          const endMs = new Date(
            dateTerm?.endDate || dateTerm?.startDate || today
          ).getTime()
          startDate = CalendarDateVO.of(new Date(startMs + offsetMs))
          endDate = CalendarDateVO.of(new Date(endMs + offsetMs))
        } else {
          let startDiff = ganttDate.getStartDateDiff(
            previousValue.current[0],
            value[0]
          )
          startDiff =
            0 < value[0] - previousValue.current[0] ? startDiff : -startDiff
          let endDiff = ganttDate.getEndDateDiff(
            previousValue.current[1],
            value[1]
          )
          endDiff = 0 < value[1] - previousValue.current[1] ? endDiff : -endDiff
          startDate = CalendarDateVO.of(
            new Date(dateTerm?.startDate || dateTerm?.endDate || today)
          )
          startDate = ganttDate.getWorkDay(startDate, startDiff) ?? startDate
          endDate = CalendarDateVO.of(
            new Date(dateTerm?.endDate || dateTerm?.startDate || today)
          )
          endDate = ganttDate.getWorkDay(endDate, endDiff) ?? endDate
          if (startDiff !== 0 && startDate.isAfter(endDate)) {
            endDate = startDate
          } else if (endDiff !== 0 && endDate.isBefore(startDate)) {
            startDate = endDate
          }
        }
        objects.setValue(row, `${dataPath}wbsItem.scheduledDate`, {
          startDate: new DateVO(startDate.date).serialize(),
          endDate: new DateVO(endDate.date).serialize(),
        })
        rows.push(row)
      })
      selectedRows.current = []
      return rows
    },
    [value, ganttDate]
  )

  const edit = useCallback(
    (data, dataPath) => {
      const isTrackDrag = moveWith.current === 'track'
      const startDateBefore = ganttDate.startDate.date
      const endDateBefore = ganttDate.endDate.date
      if (isTrackDrag && (startDisabled || endDisabled)) {
        if (!startDisabled) ganttDate.setStartDateByIndex(value[0])
        if (!endDisabled) ganttDate.setEndDateByIndex(value[1])
        const useEnd = startDisabled
        const refDate = useEnd ? dragEndDate.current : dragStartDate.current
        const newDate = useEnd
          ? ganttDate.endDate.date
          : ganttDate.startDate.date
        const deltaDays =
          refDate && newDate
            ? ganttDate.daysBetween(refDate, newDate)
            : value[0] - previousValue.current[0]
        if (startDisabled) ganttDate.shiftDateByDays(deltaDays, 'start')
        if (endDisabled) ganttDate.shiftDateByDays(deltaDays, 'end')
      } else {
        !startDisabled && ganttDate.setStartDateByIndex(value[0])
        !endDisabled && ganttDate.setEndDateByIndex(value[1])
      }
      if (ganttDate.startDate.isAfter(ganttDate.endDate)) {
        if (!endDisabled) {
          ganttDate.setEndDateByIndex(ganttDate.startIndex + 1)
        } else {
          ganttDate.setStartDateByIndex(ganttDate.endIndex)
        }
      }
      // Fix index
      const fixedValue = [ganttDate.startIndex, ganttDate.endIndex]
      if (fixedValue[0] !== value[0] || fixedValue[1] !== value[1]) {
        setValue(fixedValue)
      }
      // Update data
      objects.setValue(data, `${dataPath}wbsItem.scheduledDate`, {
        startDate: new DateVO(ganttDate.startDate.date).serialize(),
        endDate: new DateVO(ganttDate.endDate.date).serialize(),
      })
      data.isEdited = true
      let trackDiff: number | undefined
      if (isTrackDrag) {
        const movedStart = ganttDate.daysBetween(
          startDateBefore,
          ganttDate.startDate.date
        )
        trackDiff =
          movedStart !== 0
            ? movedStart
            : ganttDate.daysBetween(endDateBefore, ganttDate.endDate.date)
      }
      return [data, ...changeSelectedRows(dataPath, data.uuid, trackDiff)]
    },
    [value, ganttDate, startDisabled, endDisabled, changeSelectedRows]
  )

  return {
    value,
    setValue,
    startDisabled,
    endDisabled,

    initialize,
    moveWithTrack,
    setMoveWithTrackOrigin,
    moveWithThumb,
    getMovingRows,
    move,
    edit,
  }
}
